import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import {
  fetchProductStock,
  type ProductStock,
  type ProductStockData,
  type VariantStock,
} from '../services/productStock';
import {
  findUnavailableAddVariants,
  getAddStockDemand,
  getCartStockUsage,
  getStockUnits,
  resolveProductSoldOut,
  type AddStockItem,
  type StockItem,
} from '../services/productAvailability';
import { useCartStore } from '@/features/Cart/stores/cart';
import { getProductBySku, getProductByVariantId } from '../composables/productLookupHelper';
import type { ProductComplete } from '../types/models';

const STOCK_REFRESH_INTERVAL = 30000;
const MAX_ADD_REDRAWS = 3;

export type AddStockCheck = 'available' | 'unavailable' | 'not-refreshed';

export type AddStockRedraw<T extends AddStockItem> = (items: T[], variantIds: number[]) => T[];

let pendingStockRefresh: Promise<boolean> | null = null;
let latestRequestId = 0;
let appliedRequestId = 0;
let forcedRequestId = 0;

export const useProductStockStore = defineStore('productStockStore', () => {
  const variantStock = ref<ProductStock>({});
  const unavailableLines = ref<string[]>([]);
  const lastUpdated = shallowRef(0);
  const cartStore = useCartStore();

  const cartStockUsage = computed(() => getCartStockUsage(cartStore.cartData.items));

  const setStock = (stockData: ProductStockData) => {
    variantStock.value = stockData.variantStock;
    unavailableLines.value = stockData.unavailableLines;
    lastUpdated.value = Date.now();
  };

  const getVariantStock = (variantId: number): VariantStock | undefined =>
    variantStock.value[variantId];

  const getExactStock = (variantId: number): number | undefined => {
    const stock = variantStock.value[variantId];
    return stock?.isExact ? stock.quantity : undefined;
  };

  const getItemStockLimit = ({ variantId, properties }: StockItem): number | undefined => {
    const limits = [...getStockUnits(variantId, properties)].flatMap(([unitVariantId, units]) => {
      const exactStock = getExactStock(unitVariantId);
      return exactStock === undefined ? [] : [Math.floor(exactStock / units)];
    });
    return limits.length ? Math.min(...limits) : undefined;
  };

  const getRemainingStock = (variantId: number, excludedLineKey?: string): number | undefined => {
    const stock = variantStock.value[variantId];
    if (!stock?.isExact) return undefined;
    const usage = excludedLineKey
      ? getCartStockUsage(cartStore.cartData.items, excludedLineKey)
      : cartStockUsage.value;
    return Math.max(0, stock.quantity - (usage.get(variantId) ?? 0));
  };

  const getAddableQuantity = (
    { variantId, properties }: StockItem,
    excludedLineKey?: string,
  ): number | undefined => {
    const limits = [...getStockUnits(variantId, properties)].flatMap(([unitVariantId, units]) => {
      const remainingStock = getRemainingStock(unitVariantId, excludedLineKey);
      return remainingStock === undefined ? [] : [Math.floor(remainingStock / units)];
    });
    return limits.length ? Math.min(...limits) : undefined;
  };

  const isProductSoldOut = (product: ProductComplete): boolean =>
    resolveProductSoldOut(product, { getVariantStock, findProductBySku: getProductBySku });

  const getUnavailableComponentTitles = ({ variantId, properties }: StockItem): string[] => {
    if (!properties?._custom_bundle_details) return [];
    return [...getStockUnits(variantId, properties)].flatMap(([componentVariantId, units]) => {
      if (componentVariantId === variantId) return [];
      const exactStock = getExactStock(componentVariantId);
      if (exactStock === undefined || exactStock >= units) return [];
      const title = getProductByVariantId(componentVariantId)?.content.title;
      return title ? [title] : [];
    });
  };

  const startStockRefresh = (isForced: boolean): Promise<boolean> => {
    const requestId = ++latestRequestId;
    if (isForced) forcedRequestId = requestId;
    const request = fetchProductStock()
      .then((stock) => {
        if (requestId < forcedRequestId) return false;
        if (!stock) {
          if (requestId === forcedRequestId && appliedRequestId < requestId) lastUpdated.value = 0;
          return false;
        }
        if (requestId < appliedRequestId) return true;
        appliedRequestId = requestId;
        setStock(stock);
        return true;
      })
      .finally(() => {
        if (pendingStockRefresh === request) pendingStockRefresh = null;
      });
    pendingStockRefresh = request;
    return request;
  };

  const refreshStock = ({ force = false }: { force?: boolean } = {}): Promise<boolean> => {
    if (force) return startStockRefresh(true);
    if (pendingStockRefresh) return pendingStockRefresh;
    if (Date.now() - lastUpdated.value < STOCK_REFRESH_INTERVAL) return Promise.resolve(true);
    return startStockRefresh(false);
  };

  const refreshStockForAdd = async (): Promise<boolean> =>
    (await refreshStock({ force: true })) || refreshStock();

  const getUnavailableAddVariants = (items: AddStockItem[]): number[] =>
    findUnavailableAddVariants(items, {
      getVariantStock,
      findProductByVariantId: getProductByVariantId,
    });

  const isAddAvailable = (items: AddStockItem[]): boolean =>
    !getUnavailableAddVariants(items).length;

  const checkAddStock = async (items: AddStockItem[]): Promise<AddStockCheck> => {
    if (!(await refreshStockForAdd())) return 'not-refreshed';
    return isAddAvailable(items) ? 'available' : 'unavailable';
  };

  const redrawUntilAvailable = <T extends AddStockItem>(
    items: T[],
    redraw: AddStockRedraw<T>,
    redrawsLeft: number,
  ): { stockCheck: AddStockCheck; items: T[] } => {
    const unavailableVariantIds = getUnavailableAddVariants(items);
    if (!unavailableVariantIds.length) return { stockCheck: 'available', items };
    const redrawnItems = redrawsLeft > 0 ? redraw(items, unavailableVariantIds) : [];
    if (!redrawnItems.length) return { stockCheck: 'unavailable', items };
    return redrawUntilAvailable(redrawnItems, redraw, redrawsLeft - 1);
  };

  const checkAddStockWithRedraws = async <T extends AddStockItem>(
    items: T[],
    redraw?: AddStockRedraw<T>,
  ): Promise<{ stockCheck: AddStockCheck; items: T[] }> => {
    if (!redraw) return { stockCheck: await checkAddStock(items), items };
    if (!(await refreshStockForAdd())) return { stockCheck: 'not-refreshed', items };
    const freshlyDrawnItems = redraw(items, [...getAddStockDemand(items).keys()]);
    const drawnItems = freshlyDrawnItems.length ? freshlyDrawnItems : items;
    return redrawUntilAvailable(drawnItems, redraw, MAX_ADD_REDRAWS);
  };

  const getStockFittedAddQuantity = (item: AddStockItem): number => {
    const stockLimit = getItemStockLimit({ variantId: item.id, properties: item.properties });
    const quantity = Math.min(item.quantity, stockLimit ?? item.quantity);
    return quantity > 0 && isAddAvailable([{ ...item, quantity }]) ? quantity : 0;
  };

  return {
    variantStock,
    unavailableLines,
    lastUpdated,
    setStock,
    getVariantStock,
    getExactStock,
    getItemStockLimit,
    getRemainingStock,
    getAddableQuantity,
    isProductSoldOut,
    getUnavailableComponentTitles,
    refreshStock,
    checkAddStock,
    checkAddStockWithRedraws,
    getStockFittedAddQuantity,
  };
});
